import { format, parseISO, isValid, formatDistanceToNow } from 'date-fns';
import { tr, enUS, de, fr, es, it, ru, ar, ja, ko } from 'date-fns/locale';
import { zhCN } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { CURRENCY } from '../constants';

/**
 * Centralized date formatting utilities using date-fns library
 * All date formatting in the project should use these functions
 */

// Locale mapping for date-fns
const LOCALE_MAP: Record<string, Locale> = {
  'tr': tr,
  'tr-TR': tr,
  'en': enUS,
  'en-US': enUS,
  'en-GB': enUS,
  'de': de,
  'de-DE': de,
  'fr': fr,
  'fr-FR': fr,
  'es': es,
  'es-ES': es,
  'it': it,
  'it-IT': it,
  'ru': ru,
  'ru-RU': ru,
  'ar': ar,
  'ar-SA': ar,
  'zh': zhCN,
  'zh-CN': zhCN,
  'ja': ja,
  'ja-JP': ja,
  'ko': ko,
  'ko-KR': ko,
};

// Get user's locale for date-fns
const getUserLocale = (): Locale => {
  const browserLocale = navigator.language || navigator.languages?.[0] || CURRENCY.LOCALE;
  const localeKey = browserLocale?.split('-')[0] || CURRENCY.LOCALE; // Get language part (e.g., 'en' from 'en-US')
  return LOCALE_MAP[localeKey] || LOCALE_MAP[CURRENCY.LOCALE] || tr;
};

/**
 * Format date to display only date (day/month/year)
 * @param date - Date string or Date object
 * @param locale - Optional locale override, defaults to browser's locale
 * @returns Formatted date string or empty string for invalid dates
 */
export const formatDate = (date: string | Date, locale?: string): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    // Check if date is valid
    if (!isValid(dateObj)) {
      return '';
    }
    
    const userLocale = locale ? LOCALE_MAP[locale] || LOCALE_MAP[CURRENCY.LOCALE] : getUserLocale();
    
    return format(dateObj, 'dd/MM/yyyy', { locale: userLocale || tr });
  } catch (error) {
    console.warn('Date formatting error:', error);
    return '';
  }
};

/**
 * Format date to display both date and time
 * @param date - Date string or Date object
 * @param locale - Optional locale override, defaults to browser's locale
 * @returns Formatted date and time string or empty string for invalid dates
 */
export const formatDateTime = (date: string | Date, locale?: string): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    // Check if date is valid
    if (!isValid(dateObj)) {
      return '';
    }
    
    const userLocale = locale ? LOCALE_MAP[locale] || LOCALE_MAP[CURRENCY.LOCALE] : getUserLocale();
    
    return format(dateObj, 'dd/MM/yyyy HH:mm', { locale: userLocale || tr });
  } catch (error) {
    console.warn('Date time formatting error:', error);
    return '';
  }
};

/**
 * Format date with custom format string
 * @param date - Date string or Date object
 * @param formatString - date-fns format string (e.g., 'dd/MM/yyyy', 'MMM dd, yyyy')
 * @param locale - Optional locale override, defaults to browser's locale
 * @returns Formatted date string or empty string for invalid dates
 */
export const formatDateWithOptions = (
  date: string | Date, 
  formatString: string = 'dd/MM/yyyy',
  locale?: string
): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    // Check if date is valid
    if (!isValid(dateObj)) {
      return '';
    }
    
    const userLocale = locale ? LOCALE_MAP[locale] || LOCALE_MAP[CURRENCY.LOCALE] : getUserLocale();
    
    return format(dateObj, formatString, { locale: userLocale || tr });
  } catch (error) {
    console.warn('Date formatting with options error:', error);
    return '';
  }
};

/**
 * Format date to display only time
 * @param date - Date string or Date object
 * @param locale - Optional locale override, defaults to browser's locale
 * @returns Formatted time string or empty string for invalid dates
 */
export const formatTime = (date: string | Date, locale?: string): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    // Check if date is valid
    if (!isValid(dateObj)) {
      return '';
    }
    
    const userLocale = locale ? LOCALE_MAP[locale] || LOCALE_MAP[CURRENCY.LOCALE] : getUserLocale();
    
    return format(dateObj, 'HH:mm', { locale: userLocale || tr });
  } catch (error) {
    console.warn('Time formatting error:', error);
    return '';
  }
};

/**
 * Format date to display relative time (e.g., "2 hours ago", "3 days ago")
 * @param date - Date string or Date object
 * @param locale - Optional locale override, defaults to browser's locale
 * @returns Formatted relative time string or empty string for invalid dates
 */
export const formatRelativeTime = (date: string | Date, locale?: string): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    // Check if date is valid
    if (!isValid(dateObj)) {
      return '';
    }
    
    const userLocale = locale ? LOCALE_MAP[locale] || LOCALE_MAP[CURRENCY.LOCALE] : getUserLocale();
    
    return formatDistanceToNow(dateObj, { 
      addSuffix: true, 
      locale: userLocale || tr
    });
  } catch (error) {
    console.warn('Relative time formatting error:', error);
    return '';
  }
};

/**
 * Check if a date string is valid
 * @param dateString - Date string to validate
 * @returns True if date is valid, false otherwise
 */
export const isValidDate = (dateString: string): boolean => {
  try {
    const date = parseISO(dateString);
    return isValid(date);
  } catch {
    return false;
  }
};

/**
 * Get date placeholder based on user's locale
 * @param locale - Optional locale override, defaults to browser's locale
 * @returns Date placeholder string (e.g., "gg/aa/yyyy", "mm/dd/yyyy", "dd.mm.yyyy")
 */
export const getDatePlaceholder = (locale?: string): string => {
  try {
    const userLocale = locale ? LOCALE_MAP[locale] || LOCALE_MAP[CURRENCY.LOCALE] : getUserLocale();
    
    // Create a sample date to determine format
    const sampleDate = new Date(2024, 11, 25); // December 25, 2024
    const formatted = format(sampleDate, 'dd/MM/yyyy', { locale: userLocale || tr });
    
    // Convert to placeholder format
    return formatted
      .replace(/2024/g, 'yyyy')
      .replace(/12/g, 'aa')
      .replace(/25/g, 'gg');
  } catch (error) {
    console.warn('Date placeholder error:', error);
    return 'gg/aa/yyyy'; // Fallback to Turkish format
  }
};

/**
 * Get date formatting options for common patterns (date-fns format strings)
 */
export const DateFormatOptions = {
  SHORT_DATE: 'dd/MM/yyyy',
  LONG_DATE: 'dd MMMM yyyy',
  SHORT_DATETIME: 'dd/MM/yyyy HH:mm',
  LONG_DATETIME: 'dd MMMM yyyy HH:mm',
  TIME_ONLY: 'HH:mm',
  MONTH_YEAR: 'MMMM yyyy',
  ISO_DATE: 'yyyy-MM-dd',
  US_DATE: 'MM/dd/yyyy',
  EU_DATE: 'dd.MM.yyyy'
} as const;
